import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	Modal,
	Text,
	Group,
	Box,
	Select,
	TextInput,
	Button,
	Divider,
	ActionIcon,
} from '@mantine/core'
import Plus from 'tabler-icons-react/dist/icons/plus'
import Trash from 'tabler-icons-react/dist/icons/trash'
import Pencil from 'tabler-icons-react/dist/icons/pencil'
import { getItemOptions } from '../services/actions/experienceActions'

const PAID_PAYMENT_STATUSES = new Set([
	'paid',
	'succeeded',
	'captured',
	'complete',
	'completed',
	'partially_refunded',
	'refunded',
])
const UNPAID_PAYMENT_METHODS = new Set(['', 'none', 'free', 'not recorded'])

const hasPositivePaymentAmount = (item) => {
	const amountCents = Number(item?.paymentAmountCents)
	if (Number.isFinite(amountCents) && amountCents > 0) return true

	const rawAmount = String(item?.paymentAmount ?? '')
		.trim()
		.replace(/^\$/, '')
		.replace(/,/g, '')
	if (!rawAmount) return false

	const amount = Number(rawAmount)
	return Number.isFinite(amount) && amount > 0
}

const hasRecordedPayment = (item) => {
	const paymentStatus = String(item?.paymentStatus || '').trim().toLowerCase()
	if (PAID_PAYMENT_STATUSES.has(paymentStatus)) return true

	const paymentMethod = String(item?.paymentMethod || '').trim().toLowerCase()
	if (!UNPAID_PAYMENT_METHODS.has(paymentMethod)) return true

	return hasPositivePaymentAmount(item)
}

const getPurchasedItemKey = (item) =>
	String(
		item?.bookingItemId ||
			item?.transactionId ||
			item?.orderId ||
			item?.id ||
			'',
	).trim()

const preservePaidPurchasedItems = ({ originalItems, nextItems }) => {
	const mergedItems = Array.isArray(nextItems) ? [...nextItems] : []
	const existingKeys = new Set(mergedItems.map(getPurchasedItemKey).filter(Boolean))

	;(Array.isArray(originalItems) ? originalItems : []).forEach((item) => {
		const key = getPurchasedItemKey(item)
		if (!hasRecordedPayment(item) || !key || existingKeys.has(key)) return
		mergedItems.push(item)
		existingKeys.add(key)
	})

	return mergedItems
}

const LegacyPurchasedItemsModal = ({
	show,
	onClose,
	onSave,
	experienceSlug,
	currentItems = [],
	currentUserId,
}) => {
	const { orgSlug } = useParams()
	const [allOptions, setAllOptions] = useState([])
	const [selectedItems, setSelectedItems] = useState([])
	const [addingNew, setAddingNew] = useState(false)
	const [newItemId, setNewItemId] = useState(null)
	const [paymentMethod, setPaymentMethod] = useState('None')
	const [paymentAmount, setPaymentAmount] = useState('')
	const [editingIndex, setEditingIndex] = useState(null)

	useEffect(() => {
		if (show) {
			getItemOptions(experienceSlug, orgSlug)
				.then((options) => setAllOptions(options))
				.catch((err) => console.error('Failed to fetch item options:', err))
		}
	}, [show, experienceSlug, orgSlug])

	useEffect(() => {
		if (show) {
			setSelectedItems([...(currentItems || [])])
			setAddingNew(false)
			setNewItemId(null)
			setPaymentMethod('None')
			setPaymentAmount('')
			setEditingIndex(null)
		}
	}, [currentItems, show])

	const handleDelete = (index) => {
		const item = selectedItems[index]
		if (item?.issued || hasRecordedPayment(item)) return
		if (window.confirm('Are you sure you want to remove this item?')) {
			setSelectedItems((prev) => prev.filter((_, idx) => idx !== index))
		}
	}

	const handleEdit = (index) => {
		const item = selectedItems[index]
		setEditingIndex(index)
		setNewItemId(item.id)
		setPaymentMethod(item.paymentMethod || 'None')
		setPaymentAmount(item.paymentAmount || '')
		setAddingNew(true)
	}

	const handleAddOrUpdate = () => {
		if (!newItemId) {
			alert('Please select an item.')
			return
		}

		if (!paymentMethod) {
			alert('Please select a payment method.')
			return
		}

		if (paymentMethod !== 'None' && !paymentAmount.trim()) {
			alert('Please enter the payment amount.')
			return
		}

		const option = allOptions.find((opt) => opt.id === newItemId)
		if (!option) return

		const existingItem = editingIndex !== null ? selectedItems[editingIndex] : null
		const itemData = {
			...(existingItem || {}),
			id: option.id,
			name: option.name,
			imageUrl: option.imageUrl || '',
			description: option.description || existingItem?.description || '',
			source: existingItem?.source || 'manual',
			issued: existingItem?.issued ?? false,
			paymentMethod,
			paymentAmount: paymentMethod === 'None' ? null : paymentAmount,
			addedByUser:
				paymentMethod && paymentMethod !== 'None' ? currentUserId : '',
		}

		const updated = [...selectedItems]
		if (editingIndex !== null) {
			updated[editingIndex] = itemData
		} else {
			updated.push(itemData)
		}

		setSelectedItems(updated)
		setNewItemId(null)
		setPaymentMethod('None')
		setPaymentAmount('')
		setAddingNew(false)
		setEditingIndex(null)
	}

	const remainingOptions = allOptions.filter(
		(opt) =>
			opt.allowAddToCheckIn &&
			!selectedItems.find(
				(item, idx) => item.id === opt.id && idx !== editingIndex
			)
	)

	return (
		<Modal
			opened={show}
			onClose={onClose}
			title='Edit Purchased Items'
			centered
			size='lg'
		>
			{selectedItems.length === 0 && (
				<Text color='dimmed' fs='italic' mb='md'>
					No items selected.
				</Text>
			)}

			{selectedItems.map((item, index) => {
				const canEdit = !item.issued
				const canDelete = canEdit && !hasRecordedPayment(item)

				return (
					<Group key={`${item.id}:${index}`} position='apart' mb='xs'>
						<Text>
							{item.name}{' '}
							{item.paymentMethod && item.paymentMethod !== 'None'
								? `(Paid $${item.paymentAmount || 0} via ${item.paymentMethod})`
								: ''}
						</Text>
						{canEdit && (
							<Group spacing={4}>
								<ActionIcon size='sm' onClick={() => handleEdit(index)}>
									<Pencil size={14} />
								</ActionIcon>
								{canDelete && (
									<ActionIcon
										color='red'
										size='sm'
										onClick={() => handleDelete(index)}
									>
										<Trash size={14} />
									</ActionIcon>
								)}
							</Group>
						)}
					</Group>
				)
			})}

			{addingNew ? (
				<Box mt='md'>
					<Select
						label='Item'
						data={remainingOptions.map((opt) => ({
							value: opt.id,
							label: opt.name,
						}))}
						value={newItemId}
						onChange={setNewItemId}
						placeholder='Select an item'
						required
						mb='sm'
						disabled={editingIndex !== null}
					/>
					<Select
						label='Payment Method'
						data={['None', 'Venmo', 'Cash']}
						value={paymentMethod}
						onChange={setPaymentMethod}
						required
						mb='sm'
					/>
					{paymentMethod !== 'None' && (
						<TextInput
							label='Amount Paid'
							value={paymentAmount}
							onChange={(e) => setPaymentAmount(e.target.value)}
							placeholder='$0.00'
							mb='sm'
							required
						/>
					)}
					<Group position='right'>
						<Button
							variant='subtle'
							size='xs'
							onClick={() => {
								setAddingNew(false)
								setNewItemId(null)
								setPaymentMethod('None')
								setPaymentAmount('')
								setEditingIndex(null)
							}}
						>
							Cancel
						</Button>
						<Button size='xs' onClick={handleAddOrUpdate}>
							{editingIndex !== null ? 'Update' : 'Add'}
						</Button>
					</Group>
				</Box>
			) : (
				<Button
					variant='outline'
					leftIcon={remainingOptions.length === 0 ? null : <Plus size={16} />}
					size='xs'
					mt='md'
					onClick={() => {
						setAddingNew(true)
						setEditingIndex(null)
					}}
					disabled={remainingOptions.length === 0}
				>
					{remainingOptions.length === 0
						? 'No Options Available to Add'
						: 'Add Purchased Item'}
				</Button>
			)}

			<Divider my='md' />

			<Group position='right'>
				<Button
					onClick={() => {
						onSave(
							preservePaidPurchasedItems({
								originalItems: currentItems,
								nextItems: selectedItems,
							}),
						)
						onClose()
					}}
				>
					Save
				</Button>
			</Group>
		</Modal>
	)
}

export default LegacyPurchasedItemsModal
